import "../styles/achievements.css";
import { AchievementPayout, AchievementRow, Achievements, LevelCatalog } from "@highmarch/app";
import { AchievementDef, RARITY_IDS } from "@highmarch/domain";
import { barHtml, btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { paintedIconHtml } from "../featureIcons";
import { paidContents, rewardChest } from "../chestReveal";
import { toast } from "../toast";
import { paidLine, rewardChipsHtml } from "./eventRewards";
import { flyReward } from "./ordersSheet";
import { secHtml, sheetHeadHtml } from "../components";

/** What the Achievements sheet needs: the use cases, the clock and a way to save and redraw the hub. */
export interface AchievementsSheetCtx {
  readonly catalog: LevelCatalog;
  readonly achievements: Achievements;
  readonly now: () => number;
  /** Saves and redraws the hub (pills, the More badge). */
  readonly onChange: () => void;
  /** A claim paid (the reward cue). */
  readonly onClaim?: () => void;
}

/** The medallion glyph of each sheet section (achievements.json groups). */
export const GROUP_ICON: Readonly<Record<string, string>> = {
  campaign: "map", stars: "star", bosses: "skull", combat: "sword", towers: "tower", gear: "bag", endgame: "flame",
};
/** M36: rows counting rare creatures wear a shiny star on their medal (they sit in the Bosses section). */
const SHINY_METRICS: ReadonlySet<string> = new Set(["raresSeen", "raresKilled"]);
/** The progress ring's size on the summary card. */
const RING_PX = 74;
/** A row's medal, and a medal in the summary's tally. */
const ROW_MEDAL_PX = 46, TALLY_MEDAL_PX = 30;
const PERCENT = 100;
/** A section header's lead glyph (components.css .k-sec: 18 px beside the 20 px title). */
const SEC_ICON_PX = 18;

/** A medal's metal: what the achievement is worth (UI pass 10-21: one pale medal on every row said nothing). */
export type MedalTier = "bronze" | "silver" | "gold";
const MEDAL_ART: Readonly<Record<MedalTier, "achievementBronze" | "achievementSilver" | "achievementGold">> = { bronze: "achievementBronze", silver: "achievementSilver", gold: "achievementGold" };

/** Gold for a Legendary or Mythic chest or a commander title, silver for an Epic chest or gems, bronze for the rest. */
export function medalTier(d: AchievementDef): MedalTier {
  const chest = d.reward.chest === null ? "" : RARITY_IDS[d.reward.chest];
  if (d.reward.title || chest === "legendary" || chest === "mythic") return "gold";
  if (chest === "epic" || d.reward.gems > 0) return "silver";
  return "bronze";
}

/**
 * A medal (the painted achievement badges, WP12) in its tier's metal: dimmed while it is to do, lit with a halo once
 * done, with a green tick once claimed.
 */
export function medalHtml(state: "todo" | "ready" | "claimed", size = 44, tier: MedalTier = state === "todo" ? "bronze" : "gold"): string {
  const tick = state === "claimed" ? `<i class="tick">${icon("star", 11, "#fff")}</i>` : "";
  return `<span class="medal painted ${state} m-${tier}" style="--s:${size}px">${paintedIconHtml(MEDAL_ART[tier], size)}${tick}</span>`;
}

function rowHtml(ctx: AchievementsSheetCtx, r: AchievementRow): string {
  const level = ctx.catalog.highestLevelCleared;
  const state = r.claimed ? "claimed" : r.done ? "ready" : "todo";
  const right = r.claimed ? `<span class="got" aria-label="Claimed">${icon("star", 16, "#fff")}</span>`
    : r.done ? btnHtml("CLAIM", "gold", { className: "claim disp", data: `data-id="${r.def.id}"` }) : "";
  const title = r.def.reward.title ? `<span class="rw ttl">${icon("flag", 12)}<b>${r.def.reward.title}</b></span>` : "";
  const prog = r.done ? "" : `<div class="prog">${barHtml(r.have / r.target, "gold", r.have <= 0 ? "zero" : "")}<small>${fmtCompact(r.have)} / ${fmtCompact(r.target)}</small></div>`;
  const shiny = SHINY_METRICS.has(r.def.metric), tier = medalTier(r.def);
  const medal = medalHtml(state, ROW_MEDAL_PX, tier);
  return `<div class="arow ${state} t-${tier}${shiny ? " shiny" : ""}" data-id="${r.def.id}">${shiny ? `<span class="shinymedal">${medal}<i class="sstar">${icon("star", 12, "#fff8d8")}</i></span>` : medal}
    <div class="at"><b>${r.def.name}</b><small>${r.def.description}</small>${prog}<div class="arew">${rewardChipsHtml(ctx.catalog.balance, level, r.def.reward)}${title}</div></div>
    ${right ? `<div class="ar">${right}</div>` : ""}</div>`;
}

function summaryHtml(ctx: AchievementsSheetCtx, rows: readonly AchievementRow[]): string {
  const done = rows.filter((r) => r.done).length, ready = rows.filter((r) => r.done && !r.claimed).length;
  const deg = Math.round((done / Math.max(1, rows.length)) * 360);
  const titles = ctx.achievements.titles(), worn = ctx.achievements.title;
  // the medals won so far, by metal: what the hall of medals holds
  const tally = (["gold", "silver", "bronze"] as const).map((t) => {
    const all = rows.filter((r) => medalTier(r.def) === t), got = all.filter((r) => r.done).length;
    return `<span class="atally m-${t}${got === 0 ? " none" : ""}">${paintedIconHtml(MEDAL_ART[t], TALLY_MEDAL_PX)}<b class="disp">${got}</b><small>/${all.length}</small></span>`;
  }).join("");
  const chips = titles.length === 0 ? ""
    : `<div class="tchips"><button class="tchip${worn === "" ? " on" : ""}" data-title="">No title</button>${titles.map((t) => `<button class="tchip${t === worn ? " on" : ""}" data-title="${t}">${t}</button>`).join("")}</div>`;
  return `<section class="asum">
    <div class="ring" style="--deg:${deg}deg;--s:${RING_PX}px"><div class="in"><b class="disp">${done}</b><small>of ${rows.length}</small></div></div>
    <div class="sx"><div class="atallies">${tally}</div>${chips}
      ${ready > 1 ? btnHtml(`CLAIM ALL (${ready})`, "gold", { icon: "trophy", className: "disp claimall" }) : ""}</div>
  </section>`;
}

/**
 * More → Achievements (FINISH_PLAN WP7, DESIGN §9.7): the summary (done of all, the commander title picker), then the
 * ones ready to claim, then every section in data order with progress bars and the reward each pays. A claim pays at
 * once (a chest plays the reveal over the sheet).
 */
export function showAchievements(parent: HTMLElement, ctx: AchievementsSheetCtx): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel achievements";
  parent.append(sheet);
  const render = (): void => {
    const a = ctx.achievements;
    a.sync(ctx.now());
    const rows = a.rows(), groups = ctx.catalog.balance.achievements.groups;
    const ready = rows.filter((r) => r.done && !r.claimed);
    const done = rows.filter((r) => r.done).length;
    const sections = groups.map((g) => {
      const list = rows.filter((r) => r.def.group === g.id);
      if (list.length === 0) return "";
      const n = list.filter((r) => r.done).length;
      return `<section class="agroup" data-g="${g.id}">${secHtml(g.name, `${n} / ${list.length}`, icon(GROUP_ICON[g.id] ?? "trophy", SEC_ICON_PX))}${list.filter((r) => !(r.done && !r.claimed)).map((r) => rowHtml(ctx, r)).join("")}</section>`;
    }).join("");
    const sub = `${done} of ${rows.length} done${a.title ? ` · ${a.title}` : ""}`;
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Achievements", sub, portrait: paintedIconHtml("achievement", 52) })}
      ${summaryHtml(ctx, rows)}
      ${ready.length > 0 ? `<section class="agroup ready">${secHtml("Ready to claim", String(ready.length), icon("trophy", SEC_ICON_PX))}${ready.map((r) => rowHtml(ctx, r)).join("")}</section>` : ""}
      ${sections}`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".arow .claim")) b.onclick = () => claim(b, [b.dataset.id!]);
    sheet.querySelector<HTMLButtonElement>(".claimall")?.addEventListener("click", (e) => claim(e.currentTarget as HTMLElement, ready.map((r) => r.def.id)));
    for (const t of sheet.querySelectorAll<HTMLButtonElement>(".tchip")) t.onclick = () => { if (a.wearTitle(t.dataset.title ?? "")) { ctx.onChange(); render(); } };
  };
  const claim = (from: HTMLElement, ids: readonly string[]): void => {
    const paid = ids.map((id) => ctx.achievements.claim(id, ctx.now())).filter((p): p is AchievementPayout => p !== null);
    if (paid.length === 0) return;
    ctx.onChange();
    ctx.onClaim?.();
    for (const p of paid) flyReward(from, p.paid.gold);
    const chests = paid.filter((p) => p.paid.chest !== null);
    const line = paid.length === 1 ? `${paid[0].def.name}: ${paidLine(paid[0].paid, paid[0].gear)}` : `${paid.length} achievements claimed`;
    const reveal = (k: number): void => {
      if (k >= chests.length) { render(); return; }
      const p = chests[k], chest = p.paid.chest!;
      // over everything, not in the sheet: the battle chest's 3D chest and slot machine (M30 #10)
      rewardChest(parent, ctx.catalog.balance, chest.rarity, paidContents(p.paid, p.gear), p.def.name, () => reveal(k + 1));
    };
    toast(parent, line, 2400);
    if (chests.length > 0) reveal(0); else render();
  };
  render();
  return sheet;
}

/** The More row's note: how many are done, or what to claim. */
export function achievementsNote(a: Achievements): string {
  const rows = a.rows(), done = rows.filter((r) => r.done).length, ready = a.claimable();
  return ready > 0 ? `${ready} to claim` : `${done} of ${rows.length} · ${Math.round((done * PERCENT) / Math.max(1, rows.length))}%`;
}
